import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Loader2, Download, Eraser, Upload, Check, ScrollText } from "lucide-react";

export default function ContractSign() {
  const nav = useNavigate();
  const [template, setTemplate] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("draw"); // draw | upload
  const [uploadedSig, setUploadedSig] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    api.get("/contract/template").then((r) => {
      setTemplate(r.data);
      if (r.data.already_signed) nav("/dashboard");
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Cannot load contract");
      nav("/onboarding/pending");
    });
  }, [nav]);

  // canvas setup
  useEffect(() => {
    if (mode !== "draw" || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#000";
    const start = (e) => { drawing.current = true; const p = getPt(c, e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = getPt(c, e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); c.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); c.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", end);
    };
  }, [mode, template]);

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedSig(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!accepted) { toast.error("Please accept the agreement"); return; }
    let sig = null;
    if (mode === "draw") {
      const c = canvasRef.current;
      // detect non-empty canvas
      const ctx = c.getContext("2d");
      const data = ctx.getImageData(0, 0, c.width, c.height).data;
      const filled = data.some((_, i) => i % 4 === 3 && data[i] > 0);
      if (!filled) { toast.error("Please draw your signature"); return; }
      sig = c.toDataURL("image/png");
    } else {
      if (!uploadedSig) { toast.error("Please upload a signature image"); return; }
      sig = uploadedSig;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contract/accept", { signature_image: sig, accept_terms: true });
      toast.success("Agreement signed. Welcome aboard!");
      // brief delay so toast is visible
      setTimeout(() => nav("/dashboard"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally { setSubmitting(false); }
  };

  if (!template) return <div className="min-h-screen bg-[#05050A] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const c = template.clauses;

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5" /> Service Agreement
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Sign the agreement to unlock your AI agency</h1>
          <p className="text-white/50 mt-2">
            Plan: <strong className="text-white/80">{template.plan?.name}</strong> · ₹{(template.plan?.amount || 0).toLocaleString("en-IN")} for 30 days
          </p>
        </header>

        <section className="glass rounded-2xl p-6 max-h-96 overflow-y-auto space-y-4 text-sm text-white/70" data-testid="contract-clauses">
          <Clause title="Service Scope" body={c.service_scope} />
          <Clause title="Refund Policy" body={c.refund_policy} />
          <Clause title="Privacy Policy" body={c.privacy_policy} />
          <Clause title="AI Content Disclaimer" body={c.ai_disclaimer} />
          <Clause title="Social Media Permissions" body={c.social_terms} />
          <Clause title="Service Limitations" body={c.service_limits} />
          <Clause title="Terms & Conditions" body={c.terms} />
        </section>

        <section className="glass rounded-2xl p-6" data-testid="contract-signature">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-heading text-lg font-black flex items-center gap-2">
              <FileSignature className="h-4 w-4 text-indigo-300" /> Your signature
            </h3>
            <div className="flex gap-1 text-xs">
              <ModeBtn active={mode === "draw"} onClick={() => setMode("draw")} testid="contract-mode-draw">Draw</ModeBtn>
              <ModeBtn active={mode === "upload"} onClick={() => setMode("upload")} testid="contract-mode-upload">Upload</ModeBtn>
            </div>
          </div>

          {mode === "draw" ? (
            <div>
              <canvas ref={canvasRef} width={640} height={180}
                data-testid="contract-canvas"
                className="w-full bg-white rounded-lg border border-white/10 cursor-crosshair touch-none" />
              <button onClick={clear} data-testid="contract-clear" className="mt-2 inline-flex items-center gap-1 text-xs text-white/40 hover:text-white">
                <Eraser className="h-3 w-3" /> Clear
              </button>
            </div>
          ) : (
            <div>
              <label className="block">
                <input type="file" onChange={handleUpload} accept="image/png,image/jpeg"
                  data-testid="contract-sig-upload" className="text-sm" />
              </label>
              {uploadedSig && <img src={uploadedSig} alt="signature" className="mt-3 max-h-32 bg-white p-2 rounded" />}
            </div>
          )}
        </section>

        <label className="flex items-start gap-3 glass rounded-lg p-4 cursor-pointer" data-testid="contract-accept-checkbox">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1" data-testid="contract-accept-input" />
          <span className="text-sm text-white/70">
            I have read and agree to the MakeYourBrand.IN Service Agreement, Privacy Policy, Refund Policy and Terms of Service. I confirm I have authority to bind my organization to this agreement.
          </span>
        </label>

        <div className="flex justify-end">
          <button onClick={submit} disabled={submitting || !accepted} data-testid="contract-submit"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Accept & unlock features
          </button>
        </div>
      </div>
    </div>
  );
}

function getPt(canvas, e) {
  const rect = canvas.getBoundingClientRect();
  const sx = canvas.width / rect.width, sy = canvas.height / rect.height;
  const t = e.touches?.[0];
  const x = (t ? t.clientX : e.clientX) - rect.left;
  const y = (t ? t.clientY : e.clientY) - rect.top;
  return { x: x * sx, y: y * sy };
}

function Clause({ title, body }) {
  return (
    <div>
      <h4 className="font-semibold text-white">{title}</h4>
      <p className="text-white/60 leading-relaxed mt-1">{body}</p>
    </div>
  );
}

function ModeBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-md transition ${active ? "brand-gradient" : "glass hover:bg-white/5"}`}>
      {children}
    </button>
  );
}
